import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Loader2, Sparkles, 
  Send, CheckCircle, AlertCircle, RefreshCw,
  FileText, Calendar, Tag, ImagePlus
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import SingleImageUploader from "../SingleImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "sfaturi", label: "Sfaturi cumpărători" },
  { id: "credite", label: "Ghid credite" },
  { id: "piata", label: "Piața imobiliară" },
  { id: "legislatie", label: "Legislație" },
];

const BlogAdminPanel = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // AI Generator states
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [aiMode, setAiMode] = useState("tema"); // "tema" or "rescriere"
  const [aiInput, setAiInput] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  
  // Article form
  const [form, setForm] = useState({
    title: "",
    content: "",
    excerpt: "",
    category: "sfaturi",
    tags: "",
    thumbnail: "",
    status: "draft"
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/blog/admin/articles`);
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Eroare la încărcarea articolelor");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Titlul este obligatoriu");
      return;
    }
    if (!form.content.trim()) {
      toast.error("Conținutul este obligatoriu");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map(t => t.trim()).filter(t => t)
      };
      
      if (editingArticle) {
        await axios.put(`${API}/blog/admin/articles/${editingArticle.id}`, payload);
        toast.success("Articol actualizat!");
      } else {
        await axios.post(`${API}/blog/admin/articles`, payload);
        toast.success("Articol creat!");
      }
      
      setShowEditor(false);
      resetForm();
      fetchArticles();
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (articleId) => {
    try {
      await axios.post(`${API}/blog/admin/articles/${articleId}/publish`);
      toast.success("Articol publicat!");
      fetchArticles();
    } catch (error) {
      toast.error("Eroare la publicare");
    }
  };

  const handleDelete = async (articleId) => {
    if (!confirm("Sigur vrei să ștergi acest articol?")) return;
    
    try {
      await axios.delete(`${API}/blog/admin/articles/${articleId}`);
      toast.success("Articol șters!");
      fetchArticles();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setForm({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || "",
      category: article.category || "sfaturi",
      tags: article.tags?.join(", ") || "",
      thumbnail: article.thumbnail || "",
      status: article.status || "draft"
    });
    setShowEditor(true);
  };

  const resetForm = () => {
    setEditingArticle(null);
    setForm({
      title: "",
      content: "",
      excerpt: "",
      category: "sfaturi",
      tags: "",
      thumbnail: "",
      status: "draft"
    });
  };

  const handleAiGenerate = async () => {
    if (!aiInput.trim()) {
      toast.error("Introdu tema sau textul pentru AI");
      return;
    }
    
    setAiGenerating(true);
    setAiResult(null);
    
    try {
      const response = await axios.post(`${API}/blog/ai/generate`, {
        mode: aiMode,
        input: aiInput,
        category: form.category
      });
      
      setAiResult(response.data);
      toast.success("Articol generat cu succes!");
    } catch (error) {
      console.error("AI Error:", error);
      toast.error(error.response?.data?.detail || "Eroare la generare");
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAiResult = () => {
    if (aiResult) {
      setForm({
        ...form,
        title: aiResult.title || form.title,
        content: aiResult.content || form.content,
        excerpt: aiResult.excerpt || form.excerpt,
        tags: aiResult.tags?.join(", ") || form.tags
      });
      setShowAiGenerator(false);
      setAiResult(null);
      setAiInput("");
      toast.success("Conținut aplicat! Poți edita înainte de salvare.");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-stone-800">Articole Blog</h2>
          <p className="text-stone-500 text-sm">Gestionează articolele și generează conținut cu AI</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowAiGenerator(true)}
          >
            <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
            Generator AI
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowEditor(true); }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Articol nou
          </Button>
        </div>
      </div>

      {/* Articles List */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-lg border">
          <FileText className="w-12 h-12 mx-auto text-stone-300 mb-3" />
          <p className="text-stone-500">Nu ai articole încă</p>
          <Button 
            className="mt-4"
            onClick={() => { resetForm(); setShowEditor(true); }}
          >
            Creează primul articol
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map(article => (
            <div 
              key={article.id}
              className="bg-white rounded-lg border p-4 flex items-center justify-between"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-stone-800 truncate">{article.title}</h3>
                  <Badge variant={article.status === "published" ? "default" : "secondary"}>
                    {article.status === "published" ? "Publicat" : "Ciornă"}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-xs text-stone-500">
                  <span className="flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {CATEGORIES.find(c => c.id === article.category)?.label || article.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(article.created_at).toLocaleDateString('ro-RO')}
                  </span>
                  {article.fb_posted && (
                    <span className="flex items-center gap-1 text-blue-600">
                      <Facebook className="w-3 h-3" />
                      Postat FB
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {article.status === "draft" && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handlePublish(article.id)}
                    className="text-emerald-600 border-emerald-600"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Publică
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => handleEdit(article)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => handleDelete(article.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Article Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArticle ? "Editează articol" : "Articol nou"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Titlu *</Label>
                <Input
                  placeholder="Titlul articolului"
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Categorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Tags (separate prin virgulă)</Label>
                <Input
                  placeholder="credit, apartament, bucuresti"
                  value={form.tags}
                  onChange={(e) => setForm({...form, tags: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <Label className="flex items-center gap-2 mb-2">
                  <ImagePlus className="w-4 h-4" />
                  Imagine thumbnail
                </Label>
                <SingleImageUploader
                  image={form.thumbnail}
                  onImageChange={(url) => setForm({...form, thumbnail: url})}
                  label="Thumbnail articol"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Excerpt (rezumat scurt)</Label>
                <Textarea
                  placeholder="Un rezumat scurt al articolului..."
                  value={form.excerpt}
                  onChange={(e) => setForm({...form, excerpt: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Conținut *</Label>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowAiGenerator(true)}
                  >
                    <Sparkles className="w-3 h-3 mr-1 text-amber-500" />
                    Generează cu AI
                  </Button>
                </div>
                <Textarea
                  placeholder="Conținutul articolului (acceptă HTML)..."
                  value={form.content}
                  onChange={(e) => setForm({...form, content: e.target.value})}
                  rows={15}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-stone-400 mt-1">
                  Poți folosi HTML pentru formatare: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;strong&gt;, etc.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditor(false)}>
                Anulează
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingArticle ? "Salvează" : "Creează"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generator Dialog */}
      <Dialog open={showAiGenerator} onOpenChange={setShowAiGenerator}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Generator AI Articole
            </DialogTitle>
            <DialogDescription>
              Folosește GPT-5.2 pentru a genera sau rescrie articole
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={aiMode} onValueChange={setAiMode} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="tema">Dezvoltă temă</TabsTrigger>
              <TabsTrigger value="rescriere">Rescrie articol</TabsTrigger>
            </TabsList>
            
            <TabsContent value="tema" className="space-y-4 mt-4">
              <div>
                <Label>Tema articolului</Label>
                <Textarea
                  placeholder="Ex: Cum să alegi apartamentul potrivit în 2026 - criterii de selecție, zone recomandate în București, buget optim..."
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-stone-400 mt-1">
                  Descrie tema și ideile principale. AI va genera un articol complet.
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="rescriere" className="space-y-4 mt-4">
              <div>
                <Label>Articolul original</Label>
                <Textarea
                  placeholder="Lipește aici articolul pe care vrei să-l rescrii sau îmbunătățești..."
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  rows={8}
                />
                <p className="text-xs text-stone-400 mt-1">
                  AI va rescrie și îmbunătăți articolul păstrând ideile principale.
                </p>
              </div>
            </TabsContent>
          </Tabs>
          
          {aiResult && (
            <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <h4 className="font-semibold text-emerald-800 mb-2">{aiResult.title}</h4>
              <p className="text-sm text-emerald-700 line-clamp-3">{aiResult.excerpt}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={applyAiResult} className="bg-emerald-600">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Aplică și editează
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAiResult(null)}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Regenerează
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowAiGenerator(false)}>
              Închide
            </Button>
            <Button 
              onClick={handleAiGenerate}
              disabled={aiGenerating || !aiInput.trim()}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {aiGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Se generează...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generează articol
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogAdminPanel;
